// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    CodeEditor,
    Popover,
    Translate,
} from '@plesk/ui-library';
import { Button } from 'admin/common/components/Button/Button';
import { MASS_MAILING_FORM } from 'admin/massMailing/constants';
import { ExampleTemplateCodeEditor } from 'admin/massMailing/containers/Styles';

export const EXAMPLE_MESSAGE_TEMPLATE = `<p>Hello,</p>
<p>The email address {{ email }} is listed as the contact for some resources. We are planning
maintenance work, during which some resources may become temporarily unavailable.</p>
{% for cr in crs %}
  <p>Compute Resource <b>{{ cr.name }}</b>{% if cr.locations %} ({{ cr.locations|join(', ') }}){% endif %}
     is scheduled for maintenance.</p>
  {% if cr.vms %}
  <p>Affected servers:</p>
  <table>
    <tr>
      <th>ID</th>
      <th>Name</th>
      <th>IP</th>
      <th>Status</th>
    </tr>
    {% for vm in cr.vms %}
    <tr>
      <td>{{ vm.id }}</td>
      <td>{{ vm.name }}</td>
      <td>{{ vm.ipv4|default(vm.ipv6)|default('N/A') }}</td>
      <td>{{ vm.status|capitalize }}</td>
    </tr>
    {% endfor %}
  </table>
  {% else %}
  <p>None of your servers are affected.</p>
  {% endif %}
{% endfor %}
<p>We apologize for any inconvenience this may cause.</p>`;

export interface IExampleTemplatePopoverProps {
    currentMessage: string;
    onAdd: (message: string) => void;
}

export const ExampleTemplatePopover: React.FC<IExampleTemplatePopoverProps> = ({ currentMessage, onAdd }) => {
    const [isVisible, setVisible] = React.useState(false);

    const handleAddExample = () => {
        onAdd(currentMessage ? `${currentMessage}\n${EXAMPLE_MESSAGE_TEMPLATE}` : EXAMPLE_MESSAGE_TEMPLATE);
        setVisible(false);
    };

    return (
        <Popover
            visible={isVisible}
            onClose={() => setVisible(false)}
            width="md"
            boundariesElement="viewport"
            target={(
                <a
                    data-cy={MASS_MAILING_FORM.SHOW_EXAMPLE_LINK}
                    onClick={() => setVisible(true)}
                >
                    <Translate content="massMailing.form.showExampleLink" />
                </a>
            )}
        >
            <h3>
                <Translate content="massMailing.form.exampleTemplateTitle" />
            </h3>
            <ExampleTemplateCodeEditor height={300}>
                <CodeEditor mode={{ name: 'twig', base: 'text/html' }} readOnly={true}>
                    {EXAMPLE_MESSAGE_TEMPLATE}
                </CodeEditor>
            </ExampleTemplateCodeEditor>
            <Button data-cy={MASS_MAILING_FORM.ADD_EXAMPLE_BTN} onClick={handleAddExample}>
                <Translate content="massMailing.form.addExampleButton" />
            </Button>
        </Popover>
    );
};
